import { Component, Host, h, Prop, Watch, Element, Listen } from '@stencil/core';
import { ActionTrackingEvent, VirtualPageTrackingEvent } from '@union-investment/tracking/src/events';
import { UiCmsFaqEntryBody } from '../ui-cms-faq-entry/ui-cms-faq-entry';
import { CmsLink } from '../../types/global';

interface UiCmsFaqCategorySubCategory {
  subCategoryName: string;
  entries: UiCmsFaqEntryBody[];
}

interface UiCmsFaqCategoryBody {
  categoryHeadline: string;
  categoryLeadText?: string;
  subCategories: UiCmsFaqCategorySubCategory[];
}

@Component({
  tag: 'ui-cms-faq-category',
  styleUrl: 'ui-cms-faq-category.scss',
  shadow: false,
})
export class UiCmsFaqCategory {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsFaqCategoryBody;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Listen('sd-show')
  onAccordionShow(e: CustomEvent) {
    this.onPanelOpen(e);
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  private onPanelOpen(e: CustomEvent): void {
    const title = (e.target as HTMLUiAccordionPanelElement).summary;

    window.ui.services?.tracking?.track({
      eventInfo: {
        eventName: `Accordion vp: ${title}`,
        eventType: 'VirtualPage',
        eventCaller: this.el,
      },
      attributes: {
        customECommerceParameter: { 2: 'FAQ_Akkordeon', 45: title, 46: this.body.categoryHeadline },
        customParameter: { 4: window.location.href },
      },
    } as VirtualPageTrackingEvent);
  }

  private handleLinkClick(e: MouseEvent): void {
    const { innerText } = e.target as HTMLElement;

    window.ui?.services?.tracking?.track({
      eventInfo: {
        eventName: `Jumpmark click: ${innerText}`,
        eventType: 'Action',
        eventCaller: this.el,
      },
      category: {
        area: 'Content',
      },
      attributes: {
        elementType: 'Link',
        elementLabel: innerText,
      },
    } as ActionTrackingEvent);
  }

  private renderJumpmarks() {
    const links: CmsLink[] =
      this.body.subCategories?.map((subCategory: UiCmsFaqCategorySubCategory) => ({
        src: `#${encodeURI(subCategory.subCategoryName)}`,
        title: subCategory.subCategoryName,
        target: '_self',
      })) || [];

    return (
      <ui-cms-link-list
        onClick={(e) => {
          this.handleLinkClick(e);
        }}
        body={{ links, columns: 2, style: 'anchor' }}
      ></ui-cms-link-list>
    );
  }

  render() {
    return (
      <Host>
        <h1 class="sd-headline sd-headline--size-4xl">{this.body.categoryHeadline}</h1>
        {this.body.categoryLeadText && <p class="sd-leadtext" innerHTML={this.body.categoryLeadText}></p>}
        {this.body.subCategories.length > 1 && this.renderJumpmarks()}
        {this.body.subCategories?.map((subCategory: UiCmsFaqCategorySubCategory, i) => (
          <div class="faq-overview__category-wrapper" key={i}>
            <ui-cms-anchor id={encodeURI(subCategory.subCategoryName)}></ui-cms-anchor>
            {this.body.subCategories.length > 1 && (
              <h3 class="sd-headline sd-headline--size-lg">{subCategory.subCategoryName}</h3>
            )}
            <sd-accordion-group>
              {subCategory.entries?.map((entry, index) => (
                <sd-accordion key={index} summary={entry.question}>
                  <ui-cms-faq-entry
                    body={{ ...entry }}
                    categoryHeadline={this.body.categoryHeadline}
                    hideQuestion
                  ></ui-cms-faq-entry>
                  <slot slot="expand-icon">
                    <sd-icon
                      name="union-investment/system/chevron-bottom"
                      library={window.ui.cmsState.iconLibrary}
                    ></sd-icon>
                    <span class="sr-only">aufklappen</span>
                  </slot>
                  <slot slot="collapse-icon">
                    <sd-icon
                      name="union-investment/system/chevron-bottom"
                      library={window.ui.cmsState.iconLibrary}
                    ></sd-icon>
                    <span class="sr-only">zuklappen</span>
                  </slot>
                </sd-accordion>
              ))}
            </sd-accordion-group>
          </div>
        ))}
      </Host>
    );
  }
}
